/**
 * CommissionPayoutCandidatesPage — Issue #372.
 *
 * Monthly payout candidate list + build / confirm / cancel actions.
 * The list intentionally stays decoupled from #371 payroll batch tables;
 * `linked_batch_ref` is a free-form pointer the operator can later use to
 * tie the candidate to an external payroll batch.
 */
import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useCommissionPayoutCandidates,
  type CommissionPayoutCandidate,
} from '@/hooks/useCommission372';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  confirmed: '已确认',
  linked: '已挂账',
  cancelled: '已作废',
};

function fmtCny(v: string): string {
  try {
    return `¥${new Decimal(v).toFixed(2)}`;
  } catch {
    return v;
  }
}

export default function CommissionPayoutCandidatesPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_PAYOUT_CANDIDATE_READ,
  );
  const canBuild = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_PAYOUT_CANDIDATE_BUILD,
  );
  const canConfirm = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_PAYOUT_CANDIDATE_CONFIRM,
  );

  const {
    candidates,
    loading,
    error,
    fetchCandidates,
    buildCandidate,
    confirmCandidate,
    cancelCandidate,
  } = useCommissionPayoutCandidates();

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    if (canRead) void fetchCandidates();
  }, [canRead, fetchCandidates]);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看提成支付候选清单的权限。
      </div>
    );
  }

  async function handleBuild() {
    if (!confirm(`构建 ${year}-${String(month).padStart(2, '0')} 月度候选？`)) return;
    const out = await buildCandidate({
      periodYear: year,
      periodMonth: month,
      cutoffAt: null,
      notes: null,
    });
    if (out) void fetchCandidates();
  }

  async function handleConfirm(c: CommissionPayoutCandidate) {
    if (!confirm(`确认 ${c.periodYear}-${c.periodMonth} 候选清单？`)) return;
    const out = await confirmCandidate(c.id, {
      expectedRecordVersion: c.recordVersion,
    });
    if (out) void fetchCandidates();
  }

  async function handleCancel(c: CommissionPayoutCandidate) {
    const reason = prompt('作废原因（必填）：');
    if (!reason) return;
    const out = await cancelCandidate(c.id, {
      expectedRecordVersion: c.recordVersion,
      reason,
    });
    if (out) void fetchCandidates();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">月度提成候选 (#372)</h1>
        {canBuild && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
            <input
              type="number"
              min={1}
              max={12}
              className="w-16 rounded border border-gray-300 px-2 py-1 text-sm"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            />
            <button
              onClick={() => {
                void handleBuild();
              }}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
            >
              构建候选
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.message}
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-gray-500">期间</th>
                <th className="px-3 py-2 text-left text-gray-500">员工数</th>
                <th className="px-3 py-2 text-left text-gray-500">明细数</th>
                <th className="px-3 py-2 text-left text-gray-500">合计</th>
                <th className="px-3 py-2 text-left text-gray-500">状态</th>
                <th className="px-3 py-2 text-left text-gray-500">截止</th>
                <th className="px-3 py-2 text-right text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {candidates.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    暂无候选清单
                  </td>
                </tr>
              ) : (
                candidates.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {c.periodYear}-{String(c.periodMonth).padStart(2, '0')}
                    </td>
                    <td className="px-3 py-2">{c.employeeCount}</td>
                    <td className="px-3 py-2">{c.detailCount}</td>
                    <td className="px-3 py-2 font-semibold">
                      {fmtCny(c.totalAmountDecimal)}
                    </td>
                    <td className="px-3 py-2">
                      {STATUS_LABELS[c.status] ?? c.status}
                    </td>
                    <td className="px-3 py-2">{c.cutoffAt.slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {canConfirm && c.status === 'draft' && (
                        <button
                          className="rounded bg-green-600 px-2 py-0.5 text-xs text-white"
                          onClick={() => {
                            void handleConfirm(c);
                          }}
                        >
                          确认
                        </button>
                      )}
                      {canConfirm &&
                        c.status !== 'cancelled' &&
                        c.status !== 'linked' && (
                          <button
                            className="rounded bg-gray-500 px-2 py-0.5 text-xs text-white"
                            onClick={() => {
                              void handleCancel(c);
                            }}
                          >
                            作废
                          </button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
